import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';
import './IncomeForm.css';

function IncomeForm({ income = null, onSuccess, onCancel }) {
  const isEdit = income !== null;

  const [formData, setFormData] = useState({
    name: income?.name || '',
    category: income?.category || 'Dividends',
    amount: income?.amount || '',
    currency: income?.currency || 'EUR',
    frequency: income?.frequency || 'monthly',
    date: income?.date || new Date().toISOString().split('T')[0],
    linkedAssetId: income?.linkedAssetId || '',
    linkedAssetType: income?.linkedAssetType || '',
    notes: income?.notes || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const CATEGORIES = [
    'Dividends',
    'Rental Income',
    'Gifts',
    'Inheritance',
    'Side Business',
    'Investment Returns',
    'Royalties',
    'Interest',
    'Capital Gains',
    'Other'
  ];

  const FREQUENCIES = [
    { value: 'one-time', label: 'One-time' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.category || !formData.amount || !formData.frequency || !formData.date) {
        throw new Error('Please fill in all required fields');
      }

      // Validate amount
      if (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
        throw new Error('Amount must be a positive number');
      }

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        linkedAssetId: formData.linkedAssetId || null,
        linkedAssetType: formData.linkedAssetType || null,
      };

      if (isEdit) {
        await axios.put(`${API_BASE_URL}/income/${income.incomeId}`, payload, {
          withCredentials: true
        });
      } else {
        await axios.post(`${API_BASE_URL}/income`, payload, {
          withCredentials: true
        });
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving income:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save income');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content income-form-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Income' : 'Add New Income'}</h2>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="name">
                Income Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., AAPL Dividend, Apartment Rent"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">
                Category <span className="required">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="frequency">
                Frequency <span className="required">*</span>
              </label>
              <select
                id="frequency"
                name="frequency"
                value={formData.frequency}
                onChange={handleChange}
                required
              >
                {FREQUENCIES.map(freq => (
                  <option key={freq.value} value={freq.value}>{freq.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="amount">
                Amount <span className="required">*</span>
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="PLN">PLN (zł)</option>
                <option value="CZK">CZK (Kč)</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">
                Date Received <span className="required">*</span>
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Optional notes about this income source..."
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Income' : 'Add Income')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IncomeForm;
